import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminBlogEditPage({ mode = 'edit' }) {
    const { slug } = useParams();
    const isNew = mode === 'new';
    const navigate = useNavigate();
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [post, setPost] = useState({
        title: '', excerpt: '', content: '', tags: [], cover_image: '', author: 'DigiTech Cloud AI Team', reading_time_min: 6,
    });

    useEffect(() => {
        if (isNew) return;
        api.get(`/blog/${slug}`).then((r) => {
            setPost({
                title: r.data.title || '',
                excerpt: r.data.excerpt || '',
                content: r.data.content || '',
                tags: r.data.tags || [],
                cover_image: r.data.cover_image || '',
                author: r.data.author || 'DigiTech Cloud AI Team',
                reading_time_min: r.data.reading_time_min || 6,
            });
        }).catch(() => toast.error('Could not load post'))
          .finally(() => setLoading(false));
    }, [slug, isNew]);

    const update = (k, v) => setPost((p) => ({ ...p, [k]: v }));

    const save = async () => {
        if (post.title.trim().length < 3 || post.excerpt.trim().length < 3 || post.content.trim().length < 10) {
            toast.error('Please fill title, excerpt, and content.');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                title: post.title,
                excerpt: post.excerpt,
                content: post.content,
                tags: post.tags,
                cover_image: post.cover_image,
                author: post.author,
                reading_time_min: Number(post.reading_time_min) || 6,
            };
            if (isNew) {
                const r = await api.post('/admin/blog', payload);
                toast.success('Post created');
                navigate(`/admin/blog/${r.data.slug}`);
            } else {
                await api.patch(`/admin/blog/${slug}`, payload);
                toast.success('Post updated');
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('Delete this post?')) return;
        try {
            await api.delete(`/admin/blog/${slug}`);
            toast.success('Deleted');
            navigate('/admin/blog');
        } catch { toast.error('Delete failed'); }
    };

    if (loading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

    return (
        <div data-testid="admin-blog-edit" className="space-y-5">
            <div className="flex items-center gap-3">
                <Button asChild variant="ghost" size="sm"><Link to="/admin/blog" className="inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
                {!isNew && <Button asChild variant="ghost" size="sm"><Link to={`/blog/${slug}`} target="_blank" className="inline-flex items-center gap-1"><ExternalLink className="h-4 w-4" /> View</Link></Button>}
            </div>
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{isNew ? 'New post' : 'Edit post'}</h1>
                <div className="flex items-center gap-2">
                    {!isNew && <Button variant="ghost" onClick={remove} className="text-destructive" data-testid="admin-blog-edit-delete"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
                    <Button onClick={save} disabled={saving} data-testid="admin-blog-edit-save" className="bg-primary text-primary-foreground hover:bg-primary/90"><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}</Button>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 dt-glass rounded-xl p-5 space-y-4">
                    <Field label="Title"><Input value={post.title} onChange={(e) => update('title', e.target.value)} placeholder="Compelling title..." data-testid="admin-blog-title" className="bg-white/5 border-white/10 focus-visible:ring-primary" /></Field>
                    <Field label="Excerpt"><Textarea rows={2} value={post.excerpt} onChange={(e) => update('excerpt', e.target.value)} placeholder="Short summary shown on listing pages" data-testid="admin-blog-excerpt" className="bg-white/5 border-white/10 focus-visible:ring-primary" /></Field>
                    <Field label="Content (Markdown supported: ## headings, **bold**, - lists)"><Textarea rows={20} value={post.content} onChange={(e) => update('content', e.target.value)} placeholder="## Section\n\nWrite your article in Markdown…" data-testid="admin-blog-content" className="bg-white/5 border-white/10 focus-visible:ring-primary font-mono text-sm" /></Field>
                </div>
                <div className="dt-glass rounded-xl p-5 space-y-4 h-fit">
                    <Field label="Cover image URL"><Input value={post.cover_image} onChange={(e) => update('cover_image', e.target.value)} placeholder="https://..." className="bg-white/5 border-white/10 focus-visible:ring-primary" /></Field>
                    {post.cover_image && (<div className="rounded-lg overflow-hidden border border-white/10"><img src={post.cover_image} alt="cover" className="w-full aspect-[16/9] object-cover" /></div>)}
                    <Field label="Tags (comma separated)">
                        <Input value={(post.tags || []).join(', ')} onChange={(e) => update('tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="AI, Cloud" className="bg-white/5 border-white/10 focus-visible:ring-primary" />
                    </Field>
                    <Field label="Author"><Input value={post.author} onChange={(e) => update('author', e.target.value)} className="bg-white/5 border-white/10 focus-visible:ring-primary" /></Field>
                    <Field label="Reading time (min)"><Input type="number" min={1} max={120} value={post.reading_time_min} onChange={(e) => update('reading_time_min', e.target.value)} className="bg-white/5 border-white/10 focus-visible:ring-primary" /></Field>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">{label}</Label>
            {children}
        </div>
    );
}
